import React, { useCallback, useRef, useState, SyntheticEvent } from 'react'

import { TestingTargetProp } from '@chegg-wt/core/types/react'
import { Folder } from '@chegg-wt/core/types/folders'
import { Icon } from '@chegg-wt/core/components/Folders/styled'
import { BaseModalProps } from '../BaseModal'
import * as Styled from './styled'
import {
  DEFAULT_FOLDER_DATA,
  DEFAULT_FOLDER_MODAL_TEST_ID,
  DEFAULT_CONFIRM_CTA_TEXT,
} from './constants'

export interface FolderModalProps extends BaseModalProps, TestingTargetProp {
  title: string
  handleConfirm: (index: number, folderData: Folder) => void // TODO: Refactor this out to just use data from `folder`
  index?: number // TODO: Refactor this out to just use data from `folder`
  folder?: Folder
  confirmCTAText?: string
}

export const validateFolder = (data) => {
  const violations = []

  // validate data
  if (data.name.trim() === '') {
    violations.push('Please enter a folder name')
  }

  return violations
}

export const FolderModalBase: React.FunctionComponent<FolderModalProps> = ({
  title,
  handleConfirm,
  handleClose,
  testId = DEFAULT_FOLDER_MODAL_TEST_ID,
  index = -1,
  folder = DEFAULT_FOLDER_DATA,
  confirmCTAText = DEFAULT_CONFIRM_CTA_TEXT,
}) => {
  const folderNameInputRef = React.createRef<HTMLInputElement>()
  const folderIsPublicToggleRef = useRef({ isOn: folder.public })

  const [validations, setValidations] = useState([])
  const handleConfirmClick = useCallback(() => {
    // 1. collect all of the relevant data
    const data = {
      name: folderNameInputRef.current.value,
      public: folderIsPublicToggleRef.current.isOn,
      id: folder.id,
    }

    // 2. clear out previous validations and perform a new validation
    const currentValidations = validateFolder(data)

    if (currentValidations.length === 0) {
      // 3. everything is ok, call handleConfirm with our collected data
      handleConfirm(index, data)
    } else {
      // 4. inform the user of the current issues
      setValidations(currentValidations)
    }
  }, [
    handleConfirm,
    validateFolder,
    setValidations,
    index,
    folderNameInputRef,
    folderIsPublicToggleRef,
  ])

  const handleSubmitForm = (e: SyntheticEvent) => {
    e.preventDefault()
    handleConfirmClick()
  }
  return (
    <Styled.Container>
      <form onSubmit={handleSubmitForm} autoComplete="off">
        <Styled.Heading data-test={`${testId}-title`}>{title}</Styled.Heading>
        {validations.length > 0 &&
          validations.map((validationMessage, i) => (
            <Styled.Alert key={validationMessage}>
              <Icon icon="/next/images/icn-error-sm.svg" iconSize="20px" />
              <Styled.AlertMessage data-test={`${testId}-validation-${i}`}>
                {validationMessage}
              </Styled.AlertMessage>
            </Styled.Alert>
          ))}
        <Styled.Field>
          <Styled.BlockLabel htmlFor="folder-name">Name</Styled.BlockLabel>
          <Styled.Input
            id="folder-name"
            name="folder-name"
            type="text"
            data-test={`${testId}-name`}
            defaultValue={folder.name}
            ref={folderNameInputRef}
          />
        </Styled.Field>
        <Styled.ButtonContainer>
          <Styled.CancelButton
            type="button"
            onClick={handleClose}
            data-test={`${testId}-cancel`}
          >
            Cancel
          </Styled.CancelButton>
          <Styled.ConfirmButton
            data-test={`${testId}-confirm`}
            onClick={handleSubmitForm}
          >
            {confirmCTAText}
          </Styled.ConfirmButton>
        </Styled.ButtonContainer>
        <Styled.ButtonContainerMobile>
          <Styled.ConfirmButton
            data-test={`${testId}-confirm`}
            onClick={handleSubmitForm}
          >
            {confirmCTAText}
          </Styled.ConfirmButton>
          <Styled.CancelButton
            type="button"
            onClick={handleClose}
            data-test={`${testId}-cancel`}
          >
            Cancel
          </Styled.CancelButton>
        </Styled.ButtonContainerMobile>
      </form>
    </Styled.Container>
  )
}

export default FolderModalBase
